import { toTypedSchema } from '@vee-validate/zod'
import axios from 'axios'
import { useField, useForm } from 'vee-validate'
import { Ref, computed, defineComponent, getCurrentInstance, ref, watch } from 'vue'
import { number, object, string } from 'zod'

import {
    CsCheckbox,
    CsCopyTextLink,
    CsInputField,
    CsModal,
    CsNotification,
    CsPrimaryButton,
    CsRadioButton,
    CsTextarea,
    MsHeadline,
    MsNotification,
} from '@sunrise/components'
import { recaptcha, screenSizeDetection, useGlobalStore } from '@sunrise/shared'
import { MLM, track } from '@sunrise/tracking'
import { Components } from '@sunrise/types'

import { EstateType } from '../../ts/mapper/decoratedEstateDetailMapper.enum'
import { OfferType } from '../../ts/service/realEstateService.enum'
import { useDetailStore } from '../../ts/store/detailStoreModule/detailStoreModule'
import { getItemFromStorage, removeItemFromStorage, saveItemToStorage } from '../../ts/utils/localStorageUtils'

import MsFormSuccess from '../../static/illus/formSuccess.svg?component'
import CsInputCounter from '../CsInputCounter/CsInputCounter.vue'
import { ProgressStep } from '../MsProgressBar/MsProgressBar.interface'
import MsProgressBar from '../MsProgressBar/MsProgressBar.vue'
import {
    ContactLocalStorage,
    NumberOfFields,
    SalutationKeys,
    SalutationOptionsLabel,
    SalutationOptionsValue,
} from './MsDetailModalContact.enum'
import { SalutationOptions } from './MsDetailModalContact.interface'
import { buildMlmTrackingData, buildTealiumTrackingData } from './MsDetailModalContactTracking'

const EMAIL_ERROR_MSG = 'Bitte gib eine gültige E-Mail-Adresse ein.'
const SALUTATION_ERROR_MSG = 'Bitte wähle eine Anrede.'
const FIRST_NAME_ERROR_MSG = 'Bitte gib einen Vornamen ein.'
const LAST_NAME_ERROR_MSG = 'Bitte gib einen Nachnamen ein.'
const MESSAGE_ERROR_MSG = 'Bitte gib eine Nachricht ein.'

const SITE_KEY = '6LdqZvcZAAAAAO2mSKVf_YfxeogksV9ZXL8wG-uC'
const salutationOptions: SalutationOptions = {
    [SalutationKeys.FEMALE]: {
        label: SalutationOptionsLabel.FEMALE,
        value: SalutationOptionsValue.FEMALE,
    },
    [SalutationKeys.MALE]: {
        label: SalutationOptionsLabel.MALE,
        value: SalutationOptionsValue.MALE,
    },
    [SalutationKeys.OTHER]: {
        label: SalutationOptionsLabel.OTHER,
        value: SalutationOptionsValue.OTHER,
    },
}

export default defineComponent({
    name: 'MsDetailModalContact',
    components: {
        CsCheckbox,
        CsCopyTextLink,
        CsRadioButton,
        CsPrimaryButton,
        CsTextarea,
        CsInputCounter,
        CsNotification,
        CsInputField,
        CsModal,
        MsHeadline,
        MsFormSuccess,
        MsProgressBar,
    },
    props: {
        isVisible: {
            type: Boolean,
            required: true,
        },
        componentId: {
            type: String,
            required: true,
        },
    },
    emits: ['closeModal'],
    setup(props) {
        const currentInstance = getCurrentInstance()?.proxy
        const privacyPoliceUrl = useGlobalStore().requestContext?.globalLinks.datenschutzerklaerung
        const servicePath = useGlobalStore().urlPaths?.services
        const SERVICE_URL = `${servicePath}/contact`
        const { isScreenM } = screenSizeDetection()
        const { offerType, periodRentFieldInfoText, contactInfo, exposeeId } = useDetailStore()
        const { handleAllowToStoreCheckbox, allowToStoreCheckbox, checkStorageForFormData } = handleStorage()
        const { getToken } = handleRecaptcha()
        const formValidation = handleFormValidation()
        const { sendFormData } = handleFormSubmission(SERVICE_URL)
        const { updateProgress, numberOfSteps, currentStep, progressSteps, resetProgress } = handleProgressBar()
        const { showSuccessView, emitClosedModalEvent } = handleSuccess()
        const { scrollToError, showErrorNotification, notificationRef, errorHandler } = handleErrors()
        const { trackFormSubmission } = handleTracking(props.componentId, numberOfSteps)

        watch(
            () => props.isVisible,
            async (visible) => {
                if (visible) {
                    await currentInstance?.$nextTick()

                    const initialData = checkStorageForFormData()

                    if (initialData) {
                        formValidation.setValues(initialData)
                        updateProgress(initialData)
                    }
                } else {
                    formValidation.resetForm()
                }
            },
        )

        const onSubmit = formValidation.handleSubmit(
            async (values) => {
                const captchaToken = await getToken()
                try {
                    await sendFormData(
                        {
                            ...values,
                            captchaToken,
                        },
                        exposeeId,
                    )

                    trackFormSubmission(currentStep.value)
                    handleAllowToStoreCheckbox(values)
                    formValidation.resetForm()
                    resetProgress()
                    showSuccessView.value = true

                    showErrorNotification.value = false
                } catch {
                    await errorHandler()
                }
            },
            () => scrollToError(),
        )

        function updateSalutationValue(value: string) {
            formValidation.salutationValue.value = value
            updateProgress(formValidation.values)
        }

        return {
            ...formValidation,
            allowToStoreCheckbox,
            onSubmit,
            contactInfo,
            currentStep,
            isScreenM,
            numberOfSteps,
            offerType,
            periodRentFieldInfoText,
            privacyPoliceUrl,
            progressSteps,
            salutationOptions,
            updateProgress,
            showSuccessView,
            OfferType,
            errorState: Components.CsNotification.NotificationState.ERROR,
            notificationRef,
            showErrorNotification,
            emitClosedModalEvent,
            updateSalutationValue,
        }
    },
    computed: {
        SalutationKeys() {
            return SalutationKeys
        },
    },
})

function handleSuccess() {
    const currentInstance = getCurrentInstance()?.proxy
    const showSuccessView = ref(false)

    function emitClosedModalEvent() {
        currentInstance?.$emit('closeModal')
        showSuccessView.value = false
    }

    return {
        showSuccessView,
        emitClosedModalEvent,
    }
}

function handleProgressBar() {
    const { offerType } = useDetailStore()
    const numberOfSteps: NumberOfFields = offerType === OfferType.WAZ ? NumberOfFields.WAZ : NumberOfFields.DEFAULT
    const currentStep = ref<number>(0)
    const progressSteps: ProgressStep[] = [
        {
            colorClass: 'bg-color-alert-pending-100',
            text: 'Mit jeder Angabe erhöhst du deine Chancen.',
            lastStep: 2,
        },
        {
            colorClass: 'bg-color-brand-yellow-100',
            text: 'Weiter so, du hast fast die Hälfte geschafft.',
            lastStep: 4,
        },
        {
            colorClass: 'bg-color-alert-positive-100',
            text: 'Klasse, du kommst deinem Ziel näher.',
            lastStep: numberOfSteps - 1,
        },
        {
            colorClass: 'bg-color-alert-positive-100',
            text: 'Nun bist du optimal vorbereitet. Viel Erfolg.',
            lastStep: numberOfSteps,
        },
    ]

    function resetProgress() {
        currentStep.value = 0
    }

    function updateProgress(formData: Record<string, string>): void {
        currentStep.value = Object.entries(formData).filter(([key, value]) => {
            return key !== 'captchaToken' && !!value && filterWaz(key)
        }).length
    }

    function filterWaz(key: string): boolean {
        return offerType !== OfferType.WAZ
            ? key !== 'rentStartDate' && key !== 'maxPersons' && key !== 'rentalPeriod'
            : true
    }

    return {
        updateProgress,
        numberOfSteps,
        currentStep,
        progressSteps,
        resetProgress,
    }
}

function handleErrors() {
    const showErrorNotification = ref<boolean>(false)
    const currentInstance = getCurrentInstance()?.proxy
    const notificationRef = ref<typeof MsNotification | null>(null)

    function scrollToError(): void {
        const firstError = findFirstError()

        if (firstError) {
            firstError.scrollIntoView({ block: 'end', inline: 'center' })

            firstError.querySelector('input')?.focus()
            firstError.querySelector('textarea')?.focus()
        }
    }

    function findFirstError() {
        return document.querySelectorAll('[data-error="true"]')[0]
    }

    async function errorHandler() {
        showErrorNotification.value = true

        await currentInstance?.$nextTick()

        notificationRef.value?.$el.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' })
    }

    return { scrollToError, errorHandler, showErrorNotification, currentInstance, notificationRef }
}

function handleStorage() {
    const allowToStoreCheckbox = ref(false)

    function handleAllowToStoreCheckbox(values: any) {
        allowToStoreCheckbox.value
            ? saveItemToStorage(ContactLocalStorage.KEY, values)
            : removeItemFromStorage(ContactLocalStorage.KEY)
    }

    function checkStorageForFormData() {
        const formDataFromStorage = getItemFromStorage<Record<string, any>>(ContactLocalStorage.KEY)

        if (formDataFromStorage) {
            allowToStoreCheckbox.value = true
        }

        return formDataFromStorage
    }

    return {
        allowToStoreCheckbox,
        handleAllowToStoreCheckbox,
        checkStorageForFormData,
    }
}

function handleFormSubmission(SERVICE_URL: string) {
    const showSuccessView = ref<boolean>(false)
    const notificationRef = ref<typeof MsNotification | null>(null)

    function sendFormData(formData: any, exposeeId: number | null) {
        return axios.post(SERVICE_URL, { ...formData, realEstateId: exposeeId })
    }

    return {
        sendFormData,
        notificationRef,
        showSuccessView,
    }
}

function handleTracking(componentId: string, numberOfSteps: number) {
    const { offerType, estateType, exposeeId } = useDetailStore()
    const { requestContext } = useGlobalStore()
    const currentUrl = requestContext?.url.href

    function trackFormSubmission(currentStep: number): void {
        const mlmData = buildMlmTrackingData(componentId, exposeeId, currentUrl)
        const tealiumData = buildTealiumTrackingData(
            exposeeId,
            currentStep,
            numberOfSteps,
            offerType as OfferType,
            estateType as EstateType,
        )

        MLM.trackClick(mlmData)
        track(tealiumData)
    }

    return {
        trackFormSubmission,
    }
}

function handleRecaptcha() {
    recaptcha.initRecaptcha(SITE_KEY)

    async function getToken() {
        return await recaptcha.recaptchaChecked(SITE_KEY)
    }

    return {
        getToken,
    }
}

function handleFormValidation() {
    const schema = toTypedSchema(
        object({
            email: string({
                required_error: EMAIL_ERROR_MSG,
            }).email({ message: EMAIL_ERROR_MSG }),
            salutation: string({
                required_error: SALUTATION_ERROR_MSG,
            }),
            phone: string().optional(),
            street: string().optional(),
            zipCode: string().optional(),
            city: string().optional(),
            rentalPeriod: number().optional(),
            maxPersons: number().optional(),
            rentStartDate: string().optional(),
            firstname: string({
                required_error: FIRST_NAME_ERROR_MSG,
            }).min(1, FIRST_NAME_ERROR_MSG),
            lastname: string({
                required_error: LAST_NAME_ERROR_MSG,
            }).min(1, LAST_NAME_ERROR_MSG),
            message: string({
                required_error: MESSAGE_ERROR_MSG,
            }).min(1, MESSAGE_ERROR_MSG),
        }),
    )
    const { handleSubmit, submitCount, resetForm, values, setValues } = useForm({
        validationSchema: schema,
        validateOnMount: false,
    })

    const { value: emailValue, errorMessage: emailError } = useField('email')
    const { value: firstNameValue, errorMessage: firstNameError } = useField('firstname')
    const { value: lastNameValue, errorMessage: lastNameError } = useField('lastname')
    const { value: messageValue, errorMessage: messageError } = useField('message')
    const { value: salutationValue, errorMessage: salutationError } = useField('salutation')
    const { value: phoneValue } = useField('phone')
    const { value: streetValue } = useField('street')
    const { value: zipValue } = useField('zipCode')
    const { value: cityValue } = useField('city')
    const { value: maxPersonsValue } = useField('maxPersons')
    const { value: rentalPeriodValue } = useField('rentalPeriod')
    const { value: rentStartDateValue } = useField('rentStartDate')

    return {
        setValues,
        handleSubmit,
        emailValue,
        firstNameValue,
        lastNameValue,
        messageValue,
        salutationValue,
        phoneValue,
        streetValue,
        zipValue,
        cityValue,
        maxPersonsValue,
        rentalPeriodValue,
        rentStartDateValue,
        emailError: passiveToEagerValidation(emailError, submitCount),
        firstNameError: passiveToEagerValidation(firstNameError, submitCount),
        lastNameError: passiveToEagerValidation(lastNameError, submitCount),
        messageError: passiveToEagerValidation(messageError, submitCount),
        salutationError: passiveToEagerValidation(salutationError, submitCount),
        resetForm,
        values,
    }
}

function passiveToEagerValidation(error: Ref<string | undefined>, submitCount: Ref<number>) {
    return computed(() => (submitCount.value > 0 ? error.value : ''))
}
